import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Facebook } from 'lucide-react';
import BrandLogo from './BrandLogo';

function XLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const productLinks = [
  { label: 'Services', href: '#services' },
  { label: 'How it works', href: '#how' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'Contact', href: '#contact' },
];

const legalLinks = [
  { label: 'Privacy Policy', to: '/privacy' },
  { label: 'Terms & Conditions', to: '/terms' },
  { label: 'Cancellation & Refund', to: '/cancellation' },
];

export default function Footer() {
  return (
    <footer className="relative bg-background text-foreground border-t border-border px-8 md:px-28 py-16 md:py-20">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-5">
            <a href="#home" className="inline-flex items-center gap-2">
              <BrandLogo className="h-7 w-7" />
              <span className="text-xl font-bold tracking-tight">SprynAI</span>
            </a>
            <p className="mt-5 text-sm leading-6 text-muted-foreground max-w-sm">
              AI-powered social media marketing for teams that want to grow
              brands, engage audiences, and ship results — faster.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[
                { href: '#', Icon: Instagram, label: 'Instagram' },
                { href: '#', Icon: Linkedin, label: 'LinkedIn' },
                { href: '#', Icon: XLogo, label: 'X' },
                { href: '#', Icon: Facebook, label: 'Facebook' },
              ].map(({ href, Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-white/[0.04] transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs font-medium tracking-wide uppercase text-muted-foreground mb-4">
              Product
            </div>
            <ul className="space-y-3">
              {productLinks.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    className="text-sm text-foreground/80 hover:text-foreground transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs font-medium tracking-wide uppercase text-muted-foreground mb-4">
              Legal
            </div>
            <ul className="space-y-3">
              {legalLinks.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="text-sm text-foreground/80 hover:text-foreground transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="mailto:contact@sprynai.com"
                  className="text-sm text-foreground/80 hover:text-foreground transition-colors"
                >
                  contact@sprynai.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} SprynAI LLC. All rights reserved.</p>
          <p>5318 E Second St, Long Beach, CA 90803</p>
        </div>
      </div>
    </footer>
  );
}
